import { Form } from 'formik';
import { FormikDebounce } from '../../common/form/FormikDebounce';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { DateTime } from 'luxon';
import { Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../store';
import { appTrainingLangListActions, getAppTrainingLangList, selectAppTrainingLangList } from '../../../store/entities/appTrainingLangList';
import {
  getQcsAdminCompany,
  qcsAdminCompanyActions,
  selectQcsAdminCompany,
  selectQcsAdminCompanyState,
} from '../../../store/entities/qcsAdminCompany';
import { qcsAdminCompanyListActions } from '../../../store/entities/qcsAdminCompanyList';
import { FetchState } from '../../../store/fetchState';
import { setQcsAdminLastCompany } from '../../../utils/storage';
import { QcsLoadingButton } from '../../common/basic/QcsLoadingButton';
import { QcsMenuItem } from '../../common/basic/QcsMenuItem';
import { DetailHeader } from '../../common/DetailHeader';
import { Checkbox } from '../../common/form/Checkbox';
import { DatePicker } from '../../common/form/DatePicker';
import { FormContainer } from '../../common/form/FormContainer';
import { FormGroup } from '../../common/form/FormGroup';
import { Input } from '../../common/form/Input';
import { Select } from '../../common/form/Select';
import { SubmitButton } from '../../common/form/SubmitButton';
import * as Yup from 'yup';
import { validations } from '../../../utils/validations';
import { LanguageSelectMultiple } from '../../common/form/LanguageSelectMultiple';
import { SUPPORTED_LANGUAGES } from '../../../utils/i18n';
import { LanguageSelect } from '../../common/form/LanguageSelect';
import { PhoneInput } from '../../common/form/PhoneInput';
import { QcsBox } from '../../common/basic/QcsBox';
import { useNavigation } from '../../../utils/urlPaths';
import { aresService, companyService } from '@api/services/services';
import { ShowError } from '../../ShowError';
import { resources } from '@api/resources';
import { CreateCompanyRequest } from '@api/models/company/CreateCompanyRequest';
import { CompanyStateEnum } from '@api/models/enums/CompanyStateEnum';
import { ShowResourceContent } from '../../ShowResourceContent';
import { ConfirmByTypingModal } from '../../common/ConfirmByTypingModal';
import QcsAlert from '../../common/basic/QcsAlert';
import { formatLocalized } from '../../../utils/dateFormat';

const COMPANY_URL = import.meta.env.VITE_COMPANY_URL as string;

/** Word the admin has to type out to confirm the deletion request. */
const DELETE_CONFIRMATION_TEXT = 'delete';

/** Time of day and zone of the job that wipes companies marked for deletion. */
const DELETION_JOB_TIME = { hour: 7, minute: 5, second: 0, millisecond: 0 };
const DELETION_JOB_ZONE = 'Europe/Prague';

/**
 * When the company is actually wiped: the first run of the deletion job that happens at least
 * 24 hours after the request. Without `requestedAt` it answers for a request made right now.
 */
const getDeletionTime = (requestedAt?: string): Date => {
  const requested = requestedAt ? DateTime.fromISO(requestedAt) : DateTime.now();
  const earliest = requested.setZone(DELETION_JOB_ZONE).plus({ hours: 24 });
  const jobRun = earliest.set(DELETION_JOB_TIME);
  return (jobRun < earliest ? jobRun.plus({ days: 1 }) : jobRun).toJSDate();
};

export const QcsAdminCompanyDetail: FC = () => {
  const { t, i18n } = useTranslation();
  const [deletionModalOpen, setDeletionModalOpen] = useState(false);
  const { goBack, getBackPath: backTo } = useNavigation('/');
  const { companyId } = useParams();
  const appTrainingLangList = useAppSelector(selectAppTrainingLangList);
  const company = useAppSelector(selectQcsAdminCompany);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(appTrainingLangListActions.reload(false));

    if (companyId === 'new') {
      dispatch(qcsAdminCompanyActions.default());
    } else {
      dispatch(getQcsAdminCompany(companyId!));
    }
  }, [companyId, dispatch]);

  useEffect(() => {
    if (appTrainingLangList.state === FetchState.None && companyId === 'new') {
      dispatch(getAppTrainingLangList());
    }
  }, [appTrainingLangList.state, dispatch, companyId]);

  const handleSubmit = async (data: CreateCompanyRequest) => {
    if (companyId === 'new') {
      await companyService.create(data);
    } else {
      await companyService.update(companyId!, data);
    }

    dispatch(qcsAdminCompanyListActions.reload(true));
    goBack();
  };

  const reloadAfterDeletionChange = () => {
    dispatch(getQcsAdminCompany(companyId!));
    dispatch(qcsAdminCompanyListActions.reload(true));
  };

  const handleRequestDeletion = async () => {
    setDeletionModalOpen(false);
    await companyService.requestCompanyDeletion(companyId!);
    reloadAfterDeletionChange();
  };

  const handleCancelDeletion = async () => {
    await companyService.cancelCompanyDeletion(companyId!);
    reloadAfterDeletionChange();
  };

  const handleSwitchToCompany = async () => {
    setQcsAdminLastCompany(company.id!);

    window.location.replace(COMPANY_URL.replaceAll('{subdomain}', company.subDomain ?? ''));
  };

  const validationSchemaBase = Yup.object().shape({
    ico: validations.companyNoRequired(t),
    name: validations.stringRequired(t),
    vat: validations.companyVatNoOptional(t),
    address: Yup.object({
      street: validations.stringRequired(t),
      city: validations.stringRequired(t),
      postalCode: validations.postalCodeRequired(t),
      country: validations.stringRequired(t),
    }),
    contact: Yup.object({
      name: validations.stringRequired(t),
      lastName: validations.stringRequired(t),
      phone: validations.phoneRequired(t),
      email: validations.emailRequired(t),
    }),
  });

  const validationSchemaNewCompany = validationSchemaBase.concat(
    Yup.object().shape({
      subDomain: validations.stringRequired(t),
      subDomainAgain: validations.stringRequired(t).oneOf([Yup.ref('subDomain')], t('errors.subDomainMatch')!),
      companySettings: Yup.object({
        licenseValidFrom: validations.stringDateNotPastRequired(t),
        licenseValidTo: Yup.string().when('licenseValidFrom', ([licenseValidFrom], schema) =>
          schema.test({
            test: (licenseValidTo: string | null | undefined) => {
              return !licenseValidTo || licenseValidFrom <= licenseValidTo;
            },
            message: t('errors.dateToBeforeFrom')!,
          }),
        ),
        maxEstablishments: validations.intPositive(t),
        maxUsers: validations.intPositive(t),
        maxTraining: validations.intPositive(t),
        applicationLanguages: validations.arrayNotEmpty(t),
        defaultLanguage: validations.stringRequired(t),
        testLanguages: validations.arrayNotEmpty(t),
      }),
    }),
  );

  const isExistingCompany = companyId !== 'new';
  const deletionRequestedAt = company.deletionRequestedAt;
  // The backend refuses to delete a company that is still active (error 245), so the deletion is
  // only offered once the company has been deactivated.
  const canRequestDeletion = company.state !== CompanyStateEnum.Active;

  return (
    <>
      <DetailHeader title="admin.company.detailTitle" backTo={backTo} />
      <ShowResourceContent resource={resources.getCompany} fetchStateSelector={selectQcsAdminCompanyState}>
        {isExistingCompany && deletionRequestedAt && (
          <QcsAlert severity="warning" sx={{ width: '100%', mb: '1rem' }}>
            <Typography sx={{ fontWeight: 600 }}>{t('admin.company.deletion.bannerTitle')}</Typography>
            <Typography variant="body2">
              {t('admin.company.deletion.bannerText', {
                requestedAt: formatLocalized(deletionRequestedAt, 'dateTime', i18n),
                deleteAt: formatLocalized(getDeletionTime(deletionRequestedAt), 'dateTime', i18n),
              })}
            </Typography>
            <QcsLoadingButton
              sx={{ mt: '0.5rem' }}
              variant="outlined"
              resourceLoading={resources.cancelCompanyDeletion}
              onClick={handleCancelDeletion}
            >
              {t('admin.company.deletion.cancelButton')}
            </QcsLoadingButton>
          </QcsAlert>
        )}
        <FormikDebounce<CreateCompanyRequest>
          initialValues={{
            ...company,
            subDomain: '',
            subDomainAgain: '',
            companySettings: {
              maxEstablishments: 1,
              maxUsers: 1,
              maxTraining: 1,
              passRisksEnabled: false,
              applicationLanguages: [],
              testLanguages: [],
              licenseValidFrom: '',
              licenseValidTo: '',
              defaultLanguage: '',
              workplaceHandoverEnabled: false,
              turnstilesEnabled: false,
              allowEntryRequests: false,
              forTesting: false,
              allowAnalytics: false,
              allowSubcontractors: false,
            },
          }}
          validationSchema={companyId === 'new' ? validationSchemaNewCompany : validationSchemaBase}
          onSubmit={handleSubmit}
        >
          {({ values, setValues }) => {
            const handleLoadFromAres = async () => {
              const res = await aresService.fetchSubjectDetailsFromAres(values.ico);
              // filter out null values
              const data = Object.fromEntries(Object.entries(res.data).filter(([, value]) => value !== null));
              setValues({ ...values, ...data });
            };

            return (
              <Form>
                <FormContainer>
                  <QcsBox
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'auto',
                        sm: 'auto max-content',
                      },
                      gridTemplateRows: { xs: 'auto auto', sm: 'auto' },
                      rowGap: { xs: companyId === 'new' ? '1rem' : 0, sm: 0 },
                      columnGap: { xs: 0, sm: companyId === 'new' ? '1rem' : 0 },
                    }}
                  >
                    <Input name="ico" label={t('admin.company.ico')} maxLength={14} required removeSpaces />

                    {companyId === 'new' && (
                      <QcsLoadingButton
                        onClick={handleLoadFromAres}
                        disabled={!values.ico}
                        resourceLoading={resources.fetchSubjectDetailsFromAres}
                        variant="contained"
                        sx={{ height: '3.5rem' }}
                      >
                        {t('admin.company.loadFromAres')}
                      </QcsLoadingButton>
                    )}
                  </QcsBox>
                  <ShowError resource={resources.fetchSubjectDetailsFromAres} />

                  <Input name="name" label={t('admin.company.name')} maxLength={100} required />
                  <Input name="vat" label={t('admin.company.vat')} maxLength={14} />

                  <FormGroup name="address.title">
                    <Input name="address.street" label={t('address.street')} maxLength={100} required />
                    <Input name="address.city" label={t('address.city')} maxLength={100} required />
                    <Input name="address.postalCode" label={t('address.postalCode')} maxLength={128} required />
                    <Input name="address.country" label={t('address.country')} maxLength={100} required />
                  </FormGroup>

                  <FormGroup name="admin.company.contactPerson">
                    <Input name="contact.name" label={t('admin.company.contactPersonName')} maxLength={100} required />
                    <Input name="contact.lastName" label={t('admin.company.contactPersonLastName')} maxLength={100} required />
                    <PhoneInput name="contact.phone" label={t('admin.company.phone')} required />
                    <Input name="contact.email" label={t('admin.company.email')} maxLength={100} required />
                  </FormGroup>

                  <Select name="state" label={t('admin.company.state')}>
                    <QcsMenuItem value={CompanyStateEnum.Active}>{t('entityState.ACTIVE')}</QcsMenuItem>
                    <QcsMenuItem value={CompanyStateEnum.Inactive}>{t('entityState.INACTIVE')}</QcsMenuItem>
                    <QcsMenuItem value={CompanyStateEnum.PreActivated}>{t('entityState.PRE_ACTIVATED')}</QcsMenuItem>
                  </Select>

                  <Input name="note" label={t('admin.company.note')} maxLength={250} multiline={true} rows={4} />

                  {companyId === 'new' && (
                    <>
                      <DatePicker name="companySettings.licenseValidFrom" label={t('admin.company.licenseValidFrom')} disablePast required />
                      <DatePicker name="companySettings.licenseValidTo" label={t('admin.company.licenseValidTo')} disablePast />
                      <Input name="subDomain" label={t('admin.company.subDomain')} maxLength={100} required />
                      <Input name="subDomainAgain" label={t('admin.company.subDomainAgain')} maxLength={100} required />
                      <Input name="companySettings.maxEstablishments" label={t('admin.company.maxEstablishments')} type="number" min={1} required />
                      <Input name="companySettings.maxUsers" label={t('admin.company.maxUsers')} type="number" min={1} required />
                      <Input name="companySettings.maxTraining" label={t('admin.company.maxTraining')} type="number" min={1} required />
                      <Checkbox name="companySettings.passRisksEnabled" label={t('admin.company.passRisksEnabled')} />
                      <Checkbox name="companySettings.workplaceHandoverEnabled" label={t('admin.company.enableHandover')} />
                      <Checkbox name="companySettings.turnstilesEnabled" label={t('admin.company.turnstilesEnabled')} />
                      <Checkbox name="companySettings.allowEntryRequests" label={t('admin.company.allowEntryRequests')} />
                      <Checkbox name="companySettings.forTesting" label={t('admin.company.forTesting')} />
                      <Checkbox name="companySettings.allowAnalytics" label={t('admin.company.allowAnalytics')} />
                      <Checkbox name="companySettings.allowSubcontractors" label={t('admin.company.allowSubcontractors')} />
                      <LanguageSelectMultiple
                        name="companySettings.applicationLanguages"
                        label={t('admin.company.applicationLanguages')}
                        languages={SUPPORTED_LANGUAGES}
                        required
                      />

                      <LanguageSelect
                        name="companySettings.defaultLanguage"
                        label={t('admin.company.defaultApplicationLanguage')}
                        languages={SUPPORTED_LANGUAGES}
                        allowedLanguages={values.companySettings.applicationLanguages}
                        required
                      />

                      <LanguageSelectMultiple
                        name="companySettings.testLanguages"
                        label={t('admin.company.testLanguages')}
                        languages={appTrainingLangList.data?.content}
                        required
                      />
                    </>
                  )}

                  <ShowError resource={[resources.getCompany, resources.createCompany, resources.updateCompany]} />
                  <QcsBox sx={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                    {isExistingCompany && (
                      <QcsLoadingButton onClick={handleSwitchToCompany} variant="outlined">
                        {t('admin.company.switchButton')}
                      </QcsLoadingButton>
                    )}
                    <SubmitButton />
                  </QcsBox>
                </FormContainer>
              </Form>
            );
          }}
        </FormikDebounce>

        {isExistingCompany && !deletionRequestedAt && (
          <QcsBox
            sx={{
              maxWidth: (theme) => theme.breakpoints.values.md,
              mt: '2rem',
              p: '1rem 1.25rem',
              border: 1,
              borderColor: 'error.main',
              borderRadius: 1,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            <QcsBox sx={{ flex: '1 1 20rem' }}>
              <Typography variant="h6" color="error">
                {t('admin.company.deletion.dangerZoneTitle')}
              </Typography>
              <Typography variant="body2">{t('admin.company.deletion.dangerZoneText')}</Typography>
            </QcsBox>
            <QcsLoadingButton
              variant="outlined"
              color="error"
              disabled={!canRequestDeletion}
              resourceLoading={resources.requestCompanyDeletion}
              onClick={() => setDeletionModalOpen(true)}
            >
              {t('admin.company.deletion.button')}
            </QcsLoadingButton>
          </QcsBox>
        )}
      </ShowResourceContent>

      <ConfirmByTypingModal
        open={deletionModalOpen}
        title={t('admin.company.deletion.confirmTitle', { name: company.name })}
        instruction={t('admin.company.deletion.confirmInstruction', { text: DELETE_CONFIRMATION_TEXT })}
        confirmationText={DELETE_CONFIRMATION_TEXT}
        confirmLabel={t('admin.company.deletion.confirmButton')}
        cancelLabel={t('common.cancel')}
        confirmColor="error"
        onConfirm={handleRequestDeletion}
        onCancel={() => setDeletionModalOpen(false)}
      >
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('admin.company.deletion.confirmIrreversible')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('admin.company.deletion.confirmGrace', { deleteAt: formatLocalized(getDeletionTime(), 'dateTime', i18n) })}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('admin.company.deletion.confirmSupportNotice')}
        </Typography>
      </ConfirmByTypingModal>
    </>
  );
};
